import { CommonModule } from '@angular/common';
import { CheckoutContactComponent } from '../checkout-contact/checkout-contact.component';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { AppIconComponent } from '../../../../common/components/icon/icon.component';
import getSafeIconName from '../../../../common/functions/get-safe-icon-name';
import { QuoteDetailDto } from '../../../../core/data/dtos/quote-detail.dto';
import { QuoteInsuredPersonDto } from '../../../../core/data/dtos/quote-insured-person.dto';
import { CountryDto } from '../../../../core/data/dtos/country.dto';
import { StateDto } from '../../../../core/data/dtos/state.dto';
import { ZipFullDescDto } from '../../../../core/data/dtos/zip-full-desc.dto';
import { InsuranceQuestionGroupDto } from '../../../../core/data/dtos/insurance-question-group.dto';
import { getCategoryLabel } from '../../../../common/utils/insurance-category.util';
import { InsuranceCategory } from '../../../../core/data/dtos/insurance-type.dto';
import { NgxMaskDirective } from 'ngx-mask';
import { FormArray } from '@angular/forms';
import { CheckoutBeneficiariesComponent } from '../checkout-beneficiaries/checkout-beneficiaries.component';

@Component({
  selector: 'app-checkout-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppIconComponent, CheckoutContactComponent, CheckoutBeneficiariesComponent],
  templateUrl: './checkout-detail.component.html',
})
export class CheckoutDetailComponent {
  @Input() detail!: QuoteDetailDto;
  @Input() form!: FormGroup;
  @Input() netPremium: number | null = null;
  @Input() countries: CountryDto[] = [];
  @Input() states: StateDto[] = [];
  @Input() filteredStates: StateDto[] = [];
  @Input() filteredZipFullDescs: ZipFullDescDto[] = [];
  @Input() useAsPrimaryForAll: boolean = false;
  @Input() hideSyncOption: boolean = false;
  @Input() showContact: boolean = true;
  @Input() totalDetails: number = 1;
  @Input() isSummaryLoading: boolean = false;

  // Events to request actions handled by parent component
  @Output() openModalRequest = new EventEmitter<{
    type: 'info-person';
    title: string;
    icon: string;
    description: string;
    detailId?: number;
  }>();
  @Output() openQuestionsForGroupRequest =
    new EventEmitter<{ group: InsuranceQuestionGroupDto; detailId: number }>();
  @Output() openOtherInsuredRequest = new EventEmitter<{
    detailId: number;
    insuredPersonId: number;
    title: string;
    description: string;
  }>();

  // Change outputs that trigger logic in parent (e.g. cascading dropdowns)
  @Output() countryChange = new EventEmitter<number | null>();
  @Output() stateChange = new EventEmitter<number | null>();
  @Output() useAsPrimaryForAllChange = new EventEmitter<boolean>();

  // Expose helpers/enums to template
  readonly InsuranceCategory = InsuranceCategory;
  readonly getCategoryLabel = getCategoryLabel;
  protected readonly getSafeIconName = getSafeIconName;

  get currencySymbol(): string {
    const cur = (this.detail?.currency || 'DOP').trim().toUpperCase();
    return cur === 'USD' ? 'USD$' : 'RD$';
  }

  get otherInsured() {
    return (this.detail.insuredPeople?.filter((p) => !p.isPrimary) || [])
      .length;
  }

  get isPrimaryInfoComplete(): boolean {
    const primary = this.detail.insuredPeople?.find((p) => p.isPrimary);
    if (!primary?.person) return false;
    const p = primary.person;
    // Check key fields populated in the info-person modal
    return !!(
      p.name &&
      p.lastName &&
      p.documentNumber &&
      p.birthDate &&
      p.gender &&
      p.civilState &&
      p.nacionality &&
      p.heightCm &&
      p.weightLb
    );
  }

  /** Only question groups with phase 'Checkout' should be shown on this page */
  get checkoutQuestionGroups(): InsuranceQuestionGroupDto[] {
    const groups = this.detail.insuranceType?.questionGroups ?? [];
    return groups.filter((g) => g.phase === 'Checkout');
  }

  /** Check if a given insured person has all key personal fields filled */
  isPersonInfoComplete(ip: QuoteInsuredPersonDto): boolean {
    const p = ip?.person;
    if (!p) return false;
    return !!(p.name && p.lastName && p.documentNumber && p.birthDate && p.gender && p.civilState);
  }

  /** Check if all questions in a group have been answered */
  isQuestionGroupComplete(group: InsuranceQuestionGroupDto): boolean {
    const questions = group.questions ?? [];
    if (questions.length === 0) return false;
    const answers = this.detail.answers ?? [];
    const answeredIds = new Set(answers.filter(a => a.value != null && a.value !== '').map(a => a.questionId));
    return questions.every(q => answeredIds.has(q.id));
  }

  /** Beneficiaries list of this policy; present when the product requires beneficiaries. */
  get beneficiariesArray(): FormArray | null {
    const ctrl = this.form?.get('beneficiaries');
    return ctrl instanceof FormArray ? ctrl : null;
  }

  toggleUseAsPrimary(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.useAsPrimaryForAllChange.emit(input.checked);
  }

}
